import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';

/**
 * Normaliza a Unicode NFC todos los strings de los cuerpos JSON salientes.
 * macOS/Safari captura los acentos en forma NFD (vocal + marca combinante),
 * que se guarda "corrupto" y se renderiza mal en los .docx generados.
 * Para texto que ya viene en NFC (Windows/Linux) la normalización no cambia
 * ningún byte, por lo que es inocua.
 */
@Injectable()
export class NfcNormalizeInterceptor implements HttpInterceptor {
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const body = req.body;
    if (typeof body === 'string') {
      return next.handle(req.clone({ body: body.normalize('NFC') }));
    }
    // Safari/WebKit no normaliza el input: los campos de texto de un FormData
    // llegan en NFD. Chrome sí normaliza a NFC, por eso solo Safari fallaba.
    // Reconstruimos el FormData normalizando los valores string y dejando
    // intactos los File/Blob (adjuntos).
    if (body instanceof FormData) {
      return next.handle(req.clone({ body: this.normalizeFormData(body) }));
    }
    if (body && typeof body === 'object'
      && !(body instanceof Blob)
      && !(body instanceof ArrayBuffer)) {
      return next.handle(req.clone({ body: this.normalizeDeep(body) }));
    }
    return next.handle(req);
  }

  private normalizeFormData(formData: FormData): FormData {
    const normalized = new FormData();
    formData.forEach((value, key) => {
      if (typeof value === 'string') {
        normalized.append(key, value.normalize('NFC'));
      } else {
        // File/Blob: se conserva el nombre original si existe
        const fileName = (value as File).name;
        if (fileName !== undefined) {
          normalized.append(key, value, fileName);
        } else {
          normalized.append(key, value);
        }
      }
    });
    return normalized;
  }

  private normalizeDeep(value: any): any {
    if (typeof value === 'string') {
      return value.normalize('NFC');
    }
    if (Array.isArray(value)) {
      return value.map(item => this.normalizeDeep(item));
    }
    // solo objetos planos: no tocar File, Blob, Date, etc.
    if (value !== null && typeof value === 'object' && value.constructor === Object) {
      const normalized: any = {};
      for (const key of Object.keys(value)) {
        normalized[key] = this.normalizeDeep(value[key]);
      }
      return normalized;
    }
    return value;
  }
}
